/**
 * livechat-upload.ts
 *
 * File upload for the Live Chat widget — the VChat two-step presigned flow
 * (the single-shot `/files/upload` route is currently broken server-side):
 *
 *   1. POST {apiBase}/files/presigned-url { fileName } with the customer's
 *      chat JWT → { presignedUrl, fileUrl }.
 *   2. PUT the raw bytes straight to `presignedUrl` (object storage).
 *   3. The public `fileUrl` is then embedded in the chat message.
 *
 * The chat JWT comes from the VChat `connected` handshake event (see
 * use-livechat.ts). No React here — pure async helper.
 */
import { env } from '@/config/env';

export interface UploadedFile {
  url: string;
  name: string;
  size: number;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

/** Upload an image and return its public URL. Throws on any failure. */
export async function uploadImage(file: File, token: string | null): Promise<UploadedFile> {
  if (!file.type.startsWith('image/')) throw new Error('not an image');
  if (file.size > MAX_IMAGE_BYTES) throw new Error('image too large');

  const apiBase = `${env.chat.socketUrl}/api/v1`;
  // Unique object key so concurrent uploads of "photo.jpg" don't collide.
  const safe = file.name.replace(/[^\w.-]+/g, '_') || 'image';
  const fileName = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}-${safe}`;

  const presRes = await fetch(`${apiBase}/files/presigned-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ fileName }),
  });
  if (!presRes.ok) throw new Error(`presign HTTP ${presRes.status}`);
  const { presignedUrl, fileUrl } = (await presRes.json()) as {
    presignedUrl?: string;
    fileUrl?: string;
  };
  if (!presignedUrl || !fileUrl) throw new Error('presign response missing url');

  const putRes = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!putRes.ok) throw new Error(`upload HTTP ${putRes.status}`);

  return { url: fileUrl, name: file.name, size: file.size };
}
